import { Component, inject } from '@angular/core';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import { Store } from '@ngrx/store';
import { selectAuthUser, selectAuthVendor } from '../../../core/store/reducers';
import { ModalService } from '../../../core/services/modal.service';
import { CreatePaymentMethodModalComponent } from '../../components/create-payment-method-modal/create-payment-method-modal.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { take } from 'rxjs/operators';
import { ApiError } from '../../../core/models/common/api-error';
import { VendorsService } from '../../../core/services/vendors.service';
import { ToastService } from '../../../core/services/toast.service';
import { authActions } from 'angular/app/core/store/auth';

@Component({
  selector: 'app-payment-methods-page',
  animations: [fadeAnimation],
  template: `
    <app-page-header title="Modes de paiement internes">
      <ng-template #actions>
        <app-button (click)="createPaymentMethod()">Ajouter</app-button>
      </ng-template>
    </app-page-header>

    <div class="mt-8">
      <app-fieldset>
        <div title>Modes de paiement internes</div>
        <div description
          >Vous pouvez ajouter des modes de paiement qui seront disponibles lors
          de la saisie d'un paiement ou d'un remboursement sur une facture.<br /><br />Les
          valeurs par défaut du système sont : "Comptant", "E-banking" et "En
          ligne".</div
        >

        @if (!vendor().internal_payment_methods?.length) {
          <app-empty-state
            icon="money-check-pen"
            message="Vous n'avez pas encore créé de mode de paiement interne."
            [inline]="true"
            [cardStyle]="true"
            (buttonClicked)="createPaymentMethod()"
          ></app-empty-state>
        }

        <div class="mt-8" [@fadeAnimation]="'in'">
          <div class="space-y-6" data-cy="rates-list">
            @for (
              paymentMethod of vendor().internal_payment_methods;
              track paymentMethod
            ) {
              <div>
                <app-list-item [id]="paymentMethod" [clickableTitle]="true">
                  <div title class="font-semibold">
                    {{ paymentMethod }}
                  </div>

                  <div class="space-x-4" actions>
                    <app-icon-action
                      icon="trash"
                      [attr.data-cy]="'delete'"
                      (click)="deletePaymentMethod(paymentMethod)"
                    ></app-icon-action>
                  </div>
                </app-list-item>
              </div>
            }
          </div>
        </div>
      </app-fieldset>
    </div>
  `,
  standalone: false,
})
export class PaymentMethodsPageComponent {
  store = inject(Store);
  modalService = inject(ModalService);
  vendorsService = inject(VendorsService);
  toastService = inject(ToastService);
  vendor = this.store.selectSignal(selectAuthVendor);

  createPaymentMethod() {
    this.modalService.open(CreatePaymentMethodModalComponent, {
      title: 'Ajouter un mode de paiement',
      okAction: {
        title: 'Enregistrer',
      },
    });
  }

  deletePaymentMethod(paymentMethod: string) {
    const instance = this.modalService.open(ConfirmModalComponent, {
      title: 'Supprimer mode de paiement',
      message: 'Êtes-vous sûr·e de vouloir supprimer le mode de paiement ?',
      okAction: {
        title: 'Supprimer',
        variant: 'danger',
      },
    });
    instance.okClicked$.pipe(take(1)).subscribe(async () => {
      try {
        instance.saving = true;
        const vendor = await this.vendorsService.deletePaymentMethod({
          name: paymentMethod,
        });
        this.store.dispatch(
          authActions.findCurrentUserSuccess({
            user: { ...this.store.selectSignal(selectAuthUser)(), vendor },
          }),
        );
        this.toastService.success('Mode de paiement supprimé.');
      } catch (exception) {
        this.toastService.error((exception as ApiError).message);
      }
      instance.saving = false;
      this.modalService.close();
    });
  }

  protected readonly length = length;
}
